import React, { useState, useEffect } from "react";
import Sidebar from "../PPT/Sidebar";
import { CiCircleInfo } from "react-icons/ci";
import { FaCheckCircle } from "react-icons/fa";
import Dropdown from "../ui/Dropdown";
import { getCurrentStatusOfPPT, getSlideStatus } from "../../utils/function";

function MoveMedia({
  isOpen,
  setIsOpen,
  onMoveMediaDone,
  slidesConfig,
  activeSlide,
}) {
  const [selectedMedia, setSelectedMedia] = useState(0);
  const [mediaItems, setMediaItems] = useState([]);
  const [selectedSlide, setSelectedSlide] = useState(0);
  const [showError, setShowError] = useState(false);

  // Get dropdown options excluding current slide
  const options = slidesConfig
    ?.map((_, index) => {
      if (index === activeSlide - 1 || index === 0) return null;
      return {
        value: (index + 1).toString(),
        label: `Slide ${index + 1}`,
      };
    })
    .filter(Boolean);

  // Handle confirm and close modal
  function onConfirm(updatedConfigs) {
    onMoveMediaDone(updatedConfigs);
    setIsOpen(false);
  }

  // Update media items when active slide changes
  useEffect(() => {
    console.info("Updating media items for active slide");
    const currentSlideData = slidesConfig[activeSlide - 1]?.slideDataArray || [];
    
    const filteredMedia = currentSlideData
      .filter(
        (item) =>
          item.type === "image" ||
          item.type === "chart" ||
          item.type === "table"
        )
        .slice(0, 2);
        
        
        setMediaItems(filteredMedia);
        // Check if first media item is generated content
        if (filteredMedia[0]?.isGeneratedByContent) {
          setShowError(true);
        } else {
          setShowError(false);
        }
      }, [slidesConfig, activeSlide,isOpen]);

      // Reset state on modal open/close
      useEffect(() => {
        if (!isOpen) {
          setSelectedMedia(0);
          setMediaItems([]);
          setSelectedSlide(0);
          setShowError(false);
        }
      }, [isOpen]);
      
      return (
        <Sidebar
      rightCTA="Move Media"
      open={isOpen}
      setOpen={setIsOpen}
      title="Move Media"
      isDisabled={!selectedSlide || showError}
      onConfirm={() => {
        console.info("Processing media move between slides");
        
        // Get current PPT status
        // Step 1: Get deeply cloned pptx configs
        const { prevConfigs } = getCurrentStatusOfPPT(slidesConfig, activeSlide);
        const mediaToMoveFromSource = mediaItems[selectedMedia];
    
        const updatedConfigs = JSON.parse(JSON.stringify(prevConfigs));
   

        // Update source slide
        // get current slide and remove media from current slide
        const currentSlide = updatedConfigs[activeSlide - 1];
       
        currentSlide.slideDataArray = currentSlide.slideDataArray.filter(
          (item) => {
            if (item.type === "text") return true;
            if (mediaToMoveFromSource.path && item.path === mediaToMoveFromSource.path) return false;
            if (mediaToMoveFromSource.imageUrl && item.imageUrl === mediaToMoveFromSource.imageUrl) return false;
            return true;
          }
        );
 

        // Update source slide layout
        const updatedCurrentSlide = getSlideStatus(currentSlide);
        if (updatedCurrentSlide.isTextPresent) {
          currentSlide.layout = updatedCurrentSlide.numberOfMedia === 1 ? "v2" : "v1";
        } else {
          currentSlide.layout = updatedCurrentSlide.numberOfMedia === 1 ? "v9" : "v1";
        }
  

        // Now getting target slide
        let targetSlide = updatedConfigs[selectedSlide - 1];
 
        const targetSlideStatus = getSlideStatus(targetSlide);
 
        // if target slide has less than 2 media, then add media to target slide and change layout of target slide
        if (targetSlideStatus.numberOfMedia <= 1) {
          targetSlide.slideDataArray.push(mediaToMoveFromSource);
          targetSlide.layout = targetSlide.isTextPresent
            ? targetSlide.numberOfMedia === 0 ? "v2" : "v4"
            : targetSlide.numberOfMedia === 0 ? "v1" : "v9";
        } 
        
        
        else {
   
          // Handle case where target slide already has 2 media items
          let mediaToMoveFromTarget;
          let indexToRemove;

          // Check all objects in slideDataArray
          for (let i = targetSlide.slideDataArray.length - 1; i >= 0; i--) {
            const item = targetSlide.slideDataArray[i];
            
            // Find last object that is image/chart/table with isGeneratedByContent=false
            if ((item.type === "image" || item.type === "chart" || item.type === "table") 
                && !item.isGeneratedByContent) {
              mediaToMoveFromTarget = item;
              indexToRemove = i;
              break;
            }
          }
         

          // If no object found with isGeneratedByContent=false, take last object
          if (!mediaToMoveFromTarget) {
            const lastItem = targetSlide.slideDataArray[targetSlide.slideDataArray.length - 1];
            // Don't take object with isGeneratedByContent=true
            if (!lastItem.isGeneratedByContent) {
              mediaToMoveFromTarget = lastItem;
              indexToRemove = targetSlide.slideDataArray.length - 1;
            }
          }
         
          if (mediaToMoveFromTarget) {
            // Remove mediaToMoveFromTarget from target slide
            targetSlide.slideDataArray.splice(indexToRemove, 1);
            
            // Insert mediaToMoveFromSource at same index
            targetSlide.slideDataArray.splice(indexToRemove, 0, mediaToMoveFromSource);

            // Update layout based on text presence
            targetSlide.layout = targetSlide.isTextPresent ? "v4" : "v9";

   

            // Create new slide with moved media
            const newSlide = {
              layout: "v1",
              slideDataArray: [mediaToMoveFromTarget],
            };
      
            updatedConfigs.splice(selectedSlide, 0, newSlide);
          }
        }

        onConfirm(updatedConfigs);
      }}
    >
      <div>
        <h2 className="text-base font-bold mb-4">Choose media to move</h2>

        <div className="flex gap-2">
          {mediaItems.map((media, index) => (
            <div
              key={index}
              className={`w-1/2 border rounded-md h-[180px] relative cursor-pointer`}
              onClick={() => {
                setSelectedMedia(index);
                if (media.isGeneratedByContent) {
                  setShowError(true);
                } else {
                  setShowError(false);
                }
              }}
            >
              <img
                src={media.path || media.imageUrl}
                alt="Media content"
                className="w-full h-full object-contain rounded-md"
              />

              {selectedMedia === index && (
                <FaCheckCircle className="absolute top-2 h-5 w-5 cursor-pointer right-2 text-[#1f36c7] bg-white rounded-full font-bold" />
              )}
            </div>
          ))}
        </div>

        {showError && (
          <div className="text-red-500 mt-2 text-sm">
            The selected media is generated by the slide content and cannot be moved to another slide.
          </div>
        )}
      </div>

      <label className={`text-sm font-semibold ${showError ? 'text-gray-400' : 'text-gray-700'} mb-1 block mt-4`}>
        Select Slide
      </label>
      <Dropdown
        className="w-full border mb-4"
        options={options}
        openPosition="down"
        selectedValue={selectedSlide}
        onChange={(value) => setSelectedSlide(value)}
        disabled={showError}
      />

      <div className="p-2 bg-gray-100 flex gap-3 items-center rounded-md text-sm mt-2">
        <CiCircleInfo className="text-3xl mt-1 text-blue-500" /> If a slide
        already has 2 media, the media will be added to a new slide.
      </div>
    </Sidebar>
  );
}

export default MoveMedia;
